import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import { uid } from '@/lib/readiness/util'

// ── Personal notifications (Iteration 3 §13) ────────────────────────────────
// The app-header bell: personal notifications with read state for automatic
// dependency changes. The Alerts module stays the platform-wide feed. In the
// browser engine notifications are kept per org × user under
// `qtz-notifications`; the bell service would own them server-side.

const HOUR = 3_600_000

export type NotificationKind = 'dependency_date_moved' | 'dependency_detected' | 'coverage_drop' | 'rescan_vulnerable'

export interface ReadinessNotification {
  id: string
  org_id: string
  index_id: string
  user: string
  kind: NotificationKind
  objective_code: string
  event_id: string
  technology_id: string | null
  // Copy leads with the consequence; `cause` is the second half.
  consequence: string
  cause: string
  created_at: string
  read_at: string | null
  raises_alert: boolean // vendor date moved past the CSA date or a pinned target
  bypass_digest: boolean
}

export type Delivery = 'immediate' | 'digest'

interface NotificationStore {
  items: ReadinessNotification[]
  follows: Record<string, string[]> // `${org}::${index}::${user}` → objective codes
  delivery: Record<string, Delivery> // user → email delivery
  // Deliver to each recipient once; a repeat change to the same technology on
  // the same objective within 24 h updates the existing row (§13 guards).
  notify: (input: Omit<ReadinessNotification, 'id' | 'user' | 'created_at' | 'read_at'>, recipients: string[], at?: string) => void
  markRead: (id: string) => void
  markAllRead: (orgId: string, user: string) => void
  toggleFollow: (orgId: string, indexId: string, user: string, code: string) => void
  setDelivery: (user: string, d: Delivery) => void
  resetFor: (orgId: string) => void
}

export const followKey = (orgId: string, indexId: string, user: string) => `${orgId}::${indexId}::${user}`

export const useNotificationStore = create<NotificationStore>()(
  persist(
    (set) => ({
      items: [],
      follows: {},
      delivery: {},

      notify: (input, recipients, at = new Date().toISOString()) =>
        set((st) => {
          let items = [...st.items]
          Array.from(new Set(recipients.filter(Boolean))).forEach((user) => {
            const dup = items.find(
              (n) =>
                n.user === user &&
                n.org_id === input.org_id &&
                n.objective_code === input.objective_code &&
                n.kind === input.kind &&
                (n.technology_id ?? '') === (input.technology_id ?? '') &&
                Math.abs(new Date(at).getTime() - new Date(n.created_at).getTime()) < 24 * HOUR
            )
            if (dup) items = items.map((n) => (n === dup ? { ...n, ...input, created_at: at, read_at: null } : n))
            else items = [{ ...input, id: uid('ntf'), user, created_at: at, read_at: null }, ...items]
          })
          return { items: items.slice(0, 500) }
        }),

      markRead: (id) => set((st) => ({ items: st.items.map((n) => (n.id === id && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)) })),
      markAllRead: (orgId, user) => set((st) => ({ items: st.items.map((n) => (n.org_id === orgId && n.user === user && !n.read_at ? { ...n, read_at: new Date().toISOString() } : n)) })),
      toggleFollow: (orgId, indexId, user, code) =>
        set((st) => {
          const k = followKey(orgId, indexId, user)
          const cur = st.follows[k] ?? []
          return { follows: { ...st.follows, [k]: cur.includes(code) ? cur.filter((c) => c !== code) : [...cur, code] } }
        }),
      setDelivery: (user, d) => set((st) => ({ delivery: { ...st.delivery, [user]: d } })),
      resetFor: (orgId) =>
        set((st) => ({
          items: st.items.filter((n) => n.org_id !== orgId),
          follows: Object.fromEntries(Object.entries(st.follows).filter(([k]) => !k.startsWith(`${orgId}::`))),
        })),
    }),
    {
      name: 'qtz-notifications',
      version: 2,
      // v1 → v2: `seeded` tracked the withdrawn demo tenant's one-off notification.
      migrate: (persisted) => {
        const st = { ...((persisted ?? {}) as Record<string, unknown>) }
        delete st.seeded
        return st as unknown as NotificationStore
      },
    }
  )
)

export const notificationsFor = (items: ReadinessNotification[], orgId: string, user: string) => items.filter((n) => n.org_id === orgId && n.user === user).sort((a, b) => b.created_at.localeCompare(a.created_at))
